'use client';

import {
  Controller,
  type Control,
  type FieldValues,
  type Path,
} from 'react-hook-form';

type TextareaFieldProps<T extends FieldValues> = {
  name: Path<T>;
  label: string;
  placeholder?: string;
  required?: boolean;
  control: Control<T>;
  error?: string;
  colSpan?: 1 | 2;
  maxLength?: number;
};

const textareaBase =
  'w-full resize-none rounded-lg border border-gray-200 px-3 py-3 text-xs font-normal text-gray-600 outline-none transition-all placeholder:text-gray-400 hover:bg-gray-50/50 hover:border-royal-300 hover:shadow-sm focus:-translate-y-px focus:ring-2 focus:ring-theme-primary-light focus:ring-offset-2 focus:ring-offset-white focus:shadow-[0_0_0_3px_rgba(37,35,146,0.08)]';

const textareaError =
  'border-red-400 hover:border-red-400 focus:ring-red-300 focus:shadow-[0_0_0_3px_rgba(239,68,68,0.08)]';

export function TextareaField<T extends FieldValues>({
  name,
  label,
  placeholder,
  required,
  control,
  error,
  maxLength,
}: TextareaFieldProps<T>) {
  const fieldId = `field-${String(name).replace(/[^a-zA-Z0-9_-]/g, '-')}`;
  return (
    <div className="flex flex-col gap-1">
      <span className="flex items-center">
        <label
          htmlFor={fieldId}
          className="text-royal-500 text-xs font-semibold"
        >
          {label}
        </label>
        {required && (
          <span className="ml-1 text-xs font-semibold text-red-500">*</span>
        )}
      </span>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <span className="relative">
            <textarea
              id={fieldId}
              {...field}
              placeholder={placeholder}
              rows={5}
              maxLength={maxLength}
              className={`${textareaBase} ${error ? textareaError : ''}`}
            />
            {maxLength !== undefined && (
              <span className="absolute right-2 bottom-2 text-[10px] font-normal text-gray-400">
                {String(field.value ?? '').length}/{maxLength}
              </span>
            )}
          </span>
        )}
      />
      {error && (
        <span role="alert" className="mt-0.5 text-[10px] text-red-500">
          {error}
        </span>
      )}
    </div>
  );
}
